import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarClock } from 'lucide-react';
import { Button, Card, CardContent, CardHeader, CardTitle } from '@mlm/ui';
import { cancelMySubscription, getMySubscription, startWalletSubscription } from '../lib/api';
import { useMySlots } from '../lib/hooks/use-my-slots';

function errorCode(e: unknown): string {
  const err = e as { response?: { data?: { error?: string } } };
  return err?.response?.data?.error ?? 'error';
}
function friendlyError(code: string): string {
  switch (code) {
    case 'insufficient_cash':
      return 'Not enough balance in your cash wallet. Top up and try again.';
    case 'insufficient_gc':
      return 'Not enough GC in your wallet. Top up and try again.';
    case 'subscription_price_not_set':
      return 'The subscription price isn’t set yet. Please check back later.';
    case 'subscription_disabled':
      return 'Subscriptions aren’t available right now.';
    case 'slot_not_active':
      return 'That slot isn’t active.';
    default:
      return 'Could not subscribe. Please try again.';
  }
}

export function SubscribePage() {
  const qc = useQueryClient();
  const q = useQuery({ queryKey: ['my-subscription'], queryFn: getMySubscription });
  const { activeSlot, activeSlotsWithLabel } = useMySlots();
  const [slotId, setSlotId] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['my-subscription'] });
    qc.invalidateQueries({ queryKey: ['wallet'] });
  };
  const subscribe = useMutation({
    mutationFn: (sid: string) => startWalletSubscription(sid),
    onSuccess: () => {
      setError(null);
      invalidate();
    },
    onError: (e) => setError(errorCode(e)),
  });
  const cancel = useMutation({ mutationFn: cancelMySubscription, onSuccess: invalidate });

  if (q.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const info = q.data;

  if (!info?.enabled) {
    return (
      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle>Subscription</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">Subscriptions aren't available right now.</p>
        </CardContent>
      </Card>
    );
  }

  const sub = info.subscription;
  const isActive = sub?.isActive ?? false;
  const periodEnd = sub?.currentPeriodEnd ? new Date(sub.currentPeriodEnd) : null;
  const priceLabel = info.plan.currency === 'gc' ? `${info.plan.price} GC` : `₱${info.plan.price}`;
  const chosenSlot = slotId || activeSlot?.id || activeSlotsWithLabel[0]?.id || '';

  return (
    <div className="max-w-lg space-y-4">
      <div>
        <h1 className="text-h1 flex items-center gap-2">
          <CalendarClock className="h-6 w-6 text-primary" /> Subscription
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Subscribe to unlock the special live links (Zoom / Vimeo). {priceLabel} per month, charged
          from your slot wallet.
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {isActive ? 'Your subscription' : 'Subscribe'}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {isActive ? (
            <>
              <p className="text-sm">
                <span className="inline-block rounded bg-success/10 text-success px-2 py-0.5 text-xs font-medium">
                  Active
                </span>
                {sub?.status === 'past_due' && (
                  <span className="ml-2 text-xs text-warning">
                    (last renewal failed — top up to keep access)
                  </span>
                )}
                {sub?.status === 'canceled' && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    (cancelled — access continues until it ends)
                  </span>
                )}
              </p>
              {periodEnd && (
                <p className="text-sm text-muted-foreground">
                  Access until {periodEnd.toLocaleDateString()}.
                </p>
              )}
              <div className="flex gap-2 pt-1">
                <Button asChild size="sm">
                  <Link to="/special-links">Open special links</Link>
                </Button>
                {sub?.status !== 'canceled' && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={cancel.isPending}
                    onClick={() => cancel.mutate()}
                  >
                    {cancel.isPending ? 'Cancelling…' : 'Cancel renewal'}
                  </Button>
                )}
              </div>
            </>
          ) : (
            <>
              <div className="text-2xl font-semibold">
                {priceLabel}
                <span className="text-sm font-normal text-muted-foreground"> / month</span>
              </div>
              {activeSlotsWithLabel.length > 1 && (
                <div className="space-y-1">
                  <label className="text-xs text-muted-foreground">
                    Charge which slot's wallet?
                  </label>
                  <select
                    className="w-full h-9 rounded-md border bg-background px-2 text-sm"
                    value={chosenSlot}
                    onChange={(e) => setSlotId(e.target.value)}
                  >
                    {activeSlotsWithLabel.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
              {error && <p className="text-sm text-destructive">{friendlyError(error)}</p>}
              <Button
                disabled={!chosenSlot || subscribe.isPending}
                onClick={() => {
                  setError(null);
                  subscribe.mutate(chosenSlot);
                }}
              >
                {subscribe.isPending ? 'Subscribing…' : 'Subscribe with wallet'}
              </Button>
              <p className="text-[11px] text-muted-foreground">
                Your wallet is charged {priceLabel} now, then monthly. Cancel anytime — access lasts
                through the paid month.
              </p>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
